/* eslint-disable indent */
/* eslint-disable no-console */
import React, { useRef, useEffect, useContext, useState } from 'react';
import TrucksMapStyles from './TrucksMap.component.styles';
import { compose } from 'recompose';
import { withGlobalStyles } from 'Support/Theme/CreateInjectableStyles/withGlobalStyles';
import ServicesContext from 'Contexts/Services/Services.context';
import { createTruckDomMarker } from './utils/create-truck-dom-marker';
import { createPlantDomMarker } from './utils/create-plant-dom-marker';
import { createSiteDomMarker } from './utils/create-site-dom-marker';
import { bindActionCreators } from 'redux';
import ReactDOMServer from 'react-dom/server';
import { UI_TRUCKS_ON_MAP_TRUCK_CLICKED, UI_TRUCKS_ON_MAP_PLANT_CLICKED, UI_TRUCKS_ON_MAP_SITE_CLICKED } from '../../State/Store/UI/UI.types';
import TruckInfo from './TruckInfo.component';
import PlantInfo from './PlantInfo.component';
import SiteInfo from './SiteInfo.component';
import TruckMarker from './TruckMarker.component';
import PlantMarker from './PlantMarker.component';
import SiteMarker from './SiteMarker.component';
import { connect } from 'react-redux';
import uiActionCreators from '../../State/Store/UI/UI.actions';
import withWidth, { isWidthDown } from '@material-ui/core/withWidth';
import { MFSDR } from 'Constants/MouseFlow/SensitiveDataReplacer';
import i18nLocaleSelector from '../../State/Store/I18n/Selectors/I18nLocale.selector';

const TrucksMap = ({ classes, trucksInfo, width, actions, locale }) => {
  console.log('🗺️ TrucksMap component rendered with trucksInfo:', trucksInfo);

  const mapRef = useRef();
  const mapHereMaps = useRef();
  const siteMarker = useRef();
  const loadingMarker = useRef();
  const previousTrucksInfo = useRef();
  const [mapLoading, setMapLoading] = useState(true);
  const services = useContext(ServicesContext);
  const defaultLocation = {
    lat: -33.87165,
    lng: 151.18422,
  };

  const isSmallScreen = isWidthDown(`xs`, width);
  useEffect(() => {
    if (!window.H) {
      return;
    }
    mapHereMaps.current = services.hereMapsService.renderTrucksMap({
      nodeRef: mapRef.current,
      H: window.H,
      coords: {
        lat: defaultLocation.lat,
        lng: defaultLocation.lng,
      },
      zoomHidden: isSmallScreen,
    });

    const onResize = () => {
      mapHereMaps.current.getViewPort().resize();
    };

    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
    }
  }, []);

  const renderArrived = (map, site) => {
    console.log('🏢 renderArrived called with site:', site);
    siteMarker.current = createSiteDomMarker(
      site,
      ReactDOMServer.renderToString(<SiteMarker classes={classes} />),
      ReactDOMServer.renderToString(<SiteInfo classes={classes} site={site} trucksArrived={trucksInfo.trucksArrived} totalTrucks={trucksInfo.truckCount} isSmallScreen={isSmallScreen} />),
      centerOnMarker(UI_TRUCKS_ON_MAP_SITE_CLICKED),
      isSmallScreen,
    );
    console.log('🏢 Site marker created:', siteMarker.current);
    if (siteMarker.current) {
      map.addObject(siteMarker.current);
      console.log('✅ Site marker added to map');
    } else {
      console.log('❌ Failed to create site marker');
    }
  };

  const centerOnMarker = (type) => ({ lat, lng }) => {
    mapHereMaps.current.setCenter({ lat, lng }, true);
    actions.uiTriggered({ type });
  };

  const addMarkersAndSetViewBounds = (trucksOnMap, resizeToMarkers = false) => {
    console.log('🎯 addMarkersAndSetViewBounds called with:', {
      trucksOnMapCount: trucksOnMap.length,
      resizeToMarkers,
      siteMarker: siteMarker.current,
      loadingMarker: loadingMarker.current,
    });

    // create map objects
    const group = new H.map.Group();
    const markers = trucksOnMap.map((el) => el.mapObject);
    console.log('📍 Truck/plant markers:', markers);

    markers.push(siteMarker.current);
    if (loadingMarker.current) {
      markers.push(loadingMarker.current);
    }

    console.log('📍 All markers to add:', markers);

    // add markers to the group
    group.addObjects(markers);
    console.log('📍 Group created with markers:', group);

    mapHereMaps.current.addObject(group);
    console.log('✅ Group added to map');

    if (resizeToMarkers) {
      console.log('📏 Resizing map to fit markers');
      // get geo bounding box for the group and set it to the map
      mapHereMaps.current.getViewModel().setLookAtData({
        bounds: group.getBoundingBox(),
      });
      // some trucks might be right on the edge of the map so give a map chance to update and then zoom out a bit
      setTimeout(() => {
        console.log('🔍 Zooming out map');
        mapHereMaps.current.setZoom(mapHereMaps.current.getZoom() - 1, true);
      });
    }
  };

  const updateMap = () => {
    console.log('🔄 updateMap called with trucksInfo:', trucksInfo);

    if (!trucksInfo) {
      console.log('⏭️ Skipping updateMap - trucksInfo is null');
      return;
    }

    // Check if we have valid site coordinates
    if (!trucksInfo.site || !trucksInfo.site.location ||
        !trucksInfo.site.location.lat || !trucksInfo.site.location.lng ||
        trucksInfo.site.location.lat === 0 || trucksInfo.site.location.lng === 0) {
      console.log('⏭️ Skipping updateMap - invalid site coordinates');
      return;
    }

    const shouldResizeMap = !previousTrucksInfo.current;
    console.log('📏 Should resize map:', shouldResizeMap);

    // do not update map if previous trucks are same as trucks already displayed on a map
    if (JSON.stringify(previousTrucksInfo.current) === JSON.stringify(trucksInfo)) {
      console.log('⏭️ Skipping map update - same data as previous');
      return;
    }

    previousTrucksInfo.current = trucksInfo;
    console.log('🧹 Removing existing map objects');
    mapHereMaps.current.removeObjects(mapHereMaps.current.getObjects());

    // add markers to the group
    console.log('🚛 Processing trucks for map:', trucksInfo.trucks);
    const trucksOnMap = trucksInfo.trucks
      .filter((el) => el.status === `INTRANSIT`)
      .reduce((acc, el, i) => {
        console.log(`🚚 Processing truck ${i + 1}:`, el);
        const { marker, truck } = createTruckDomMarker(
          el,
          ReactDOMServer.renderToString(<TruckMarker classes={classes} truck={el} />),
          ReactDOMServer.renderToString(
            <TruckInfo
              classes={classes}
              truck={el}
              truckIndex={i + (trucksInfo.trucksArrived ? trucksInfo.trucksArrived * 1 : 0) + trucksInfo._invalidTrucks}
              lang={locale.lang}
              totalTrucks={trucksInfo.truckCount}
              isSmallScreen={isSmallScreen}
            />,
          ),
          centerOnMarker(UI_TRUCKS_ON_MAP_TRUCK_CLICKED),
          isSmallScreen,
        );

        console.log('✅ Truck marker created:', marker);
        acc.push({
          ...truck,
          mapObject: marker,
        });
        mapHereMaps.current.addObject(marker);
        return acc;
      }, []);

    console.log(`🚛 Total trucks added to map: ${trucksOnMap.length}`);
    console.log('🏢 Processing site for map:', trucksInfo.site);
    renderArrived(mapHereMaps.current, trucksInfo.site);

    console.log('🏭 Processing plants for map:', trucksInfo.plants);
    const plantsOnMap = trucksInfo.plants.reduce((acc, el, i) => {
      console.log(`🏭 Processing plant ${i + 1}:`, el);
      const plantInfoHtml = ReactDOMServer.renderToString(
        <PlantInfo classes={classes} plant={el} />,
      );

      const { marker, plant } = createPlantDomMarker(
        el,
        ReactDOMServer.renderToString(<PlantMarker classes={classes} plant={el} />),
        plantInfoHtml,
        centerOnMarker(UI_TRUCKS_ON_MAP_PLANT_CLICKED),
        isSmallScreen,
      );

      console.log('✅ Plant marker created:', marker);
      acc.push({
        ...plant,
        mapObject: marker,
      });
      mapHereMaps.current.addObject(marker);
      return acc;
    }, []);

    console.log(`🏭 Total plants added to map: ${plantsOnMap.length}`);
    console.log('🎯 Adding markers and setting view bounds');
    addMarkersAndSetViewBounds(trucksOnMap.concat(plantsOnMap), shouldResizeMap);
    console.log('✅ Map update completed');
  };

  useEffect(() => {
    console.log('🔄 TrucksMap useEffect triggered - trucksInfo changed:', trucksInfo);
    if (!window.H) {
      console.log('❌ HERE Maps API not loaded (window.H not available)');
      return;
    }
    if (!trucksInfo) {
      console.log('⏭️ Skipping map update - no trucksInfo data');
      return;
    }
    updateMap();
  }, [trucksInfo]);

  return (
    <div className={classes.mainWrapper} data-mf-replace-inner={MFSDR}>
      <div ref={mapRef} className={classes.map} />
    </div>
  );
};

export default compose(
  withWidth(),
  withGlobalStyles(TrucksMapStyles),
  connect(
    (state) => ({
      locale: i18nLocaleSelector(state),
    }),
    (dispatch) => ({
      actions: bindActionCreators(
        {
          ...uiActionCreators,
        },
        dispatch,
      ),
    }),
  ),
)(TrucksMap);
